import View from '@play-co/timestep-core/lib/ui/View';
import uiConfig from 'src/lib/ui/config';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import bitmapFonts from 'src/lib/bitmapFonts';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import {
  ImageScaleView,
  ImageScaleViewOpts,
  ImageSliceView,
  ImageView,
  ImageViewOpts,
} from '@play-co/timestep-core/ui';
import { TextInput } from 'src/game/components/shared/TextInput';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { Pet } from 'src/replicant/buruburu/pet';
import { getTimestampText } from 'src/game/visit/visit.util';
import { duration } from 'src/replicant/utils/duration';
import { Duo } from 'src/replicant/duo';
import StateObserver from 'src/StateObserver';
import { getCreativeSuffix } from 'src/lib/utils';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';

namespace style {
  export const profileBg = {
    image: 'assets/ui/shared/lossless/scroller_panel_blue.png',
    y: 170,
    width: 520,
    height: 132,
    slices: {
      top: 19,
      left: 19,
      right: 22,
      bot: 28,
    },
  };

  export const profilePicture = {
    ...uiConfig.buruburu.invite.friendTile.image,
    width: 85,
    height: 90,
    image: 'assets/ui/shared/icons/icon_avatar_generic.png',
    centerOnOrigin: true,
    x: 60,
    y: 66,
  };

  export const profileName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    x: 110,
    y: 25,
  };

  export const petName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    x: 150,
    y: 7,
  };

  export const petDetails: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#BF8147',
    x: 150,
    y: 49,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const itemName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 350,
    x: 150,
    y: 29,
  };

  export const itemDetails: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#BF8147',
    x: 150,
    y: 53,
    width: 240,
    height: 34,
    align: 'right',
  };

  export const responseLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 170,
    x: 20,
    y: 600,
    verticalAlign: 'center',
  };

  export const pane = {
    ...uiConfig.buruburu.profile.profile.contentPanel,
    height: 100,
    centerOnOrigin: true,
  };

  export const visitsLabel: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#7191AB',
    x: 110,
    y: 67,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const lastLoginLabel: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#7191AB',
    x: 298,
    y: 86,
    width: 200,
    height: 30,
    align: 'right',
    verticalAlign: 'center',
  };

  export const responseContainer = {
    ...uiConfig.buruburu.profile.profile.contentPanel,
    x: 375,
    y: 625,
    width: 370,
    height: 62,
    align: 'right',
    centerOnOrigin: true,
  } as const;

  export const messageLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 371,
    height: 20,
    size: 20,
    x: 24,
    y: 679,
  };

  export const maxCharactersLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 371,
    height: 20,
    size: 13,
    x: 177,
    y: 679,
    align: 'right',
    verticalAlign: 'bottom',
  };

  export const textInput = {
    y: 756,
    height: 102,
    centerOnOrigin: true,
    inputStyle: {
      background: '#d47a5a',
      boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '5px',
      color: '#ffffff',
      textAlign: 'center',
      fontSize: '10vm',
      padding: '10px 5px 5px 10px',
    },
    maxLength: 48,
  };

  export const messageDisplayedLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 500,
    height: 40,
    size: 20,
    x: 38,
    y: 600,
    wordWrap: true,
    align: 'center',
  };

  export const dashedLine: ImageViewOpts = {
    image: 'assets/ui/shared/lossless/dashed_line.png',
    width: 389,
    height: 10,
    centerOnOrigin: true,
  };

  export const speciesIconBackdrop: ImageViewOpts = {
    image: 'assets/ui/shared/lossless/backdrop_panel.png',
    width: 120,
    height: 120,
    x: 22,
    y: -10,
  };

  export const speciesIcon: ImageScaleViewOpts = {
    x: speciesIconBackdrop.width / 2,
    y: speciesIconBackdrop.height / 2,
    width: speciesIconBackdrop.width - 10,
    height: speciesIconBackdrop.height - 10,
    scaleMethod: 'contain',
    centerOnOrigin: true,
  };

  export const itemIcon: ImageScaleViewOpts = {
    width: 110,
    height: 120,
    x: 22,
    y: -10,
    scaleMethod: 'contain',
  };

  export const gemPanel: ImageScaleViewOpts = {
    image: 'assets/ui/shared/frames/currency_frame.png',
    scaleMethod: '9slice',
    sourceSlices: {
      horizontal: { left: 18, right: 18 },
      vertical: { top: 18, bottom: 18 },
    },
    x: 397,
    y: 40,
    width: 130,
    height: 50,
  };

  export const gemIcon: ImageScaleViewOpts = {
    scaleMethod: 'contain',
    image: 'assets/ui/hud/icons/hud_gem.png',
    width: 50,
    height: 50,
    x: 2,
  };

  export const gemText: LangBitmapOpts = {
    size: 25,
    font: 'Body',
    color: '#ffffff',
    width: 123,
    height: 50,
    align: 'right',
    verticalAlign: 'center',
  };
}

export default class PopupDuoReminderConfirmation extends PopupBasic {
  private profilePicture: ImageScaleView;
  private profileName: LangBitmapFontTextView;
  private visits: LangBitmapFontTextView;
  private lastLogin: LangBitmapFontTextView;
  private petName: LangBitmapFontTextView;
  private petDetails: LangBitmapFontTextView;
  private petSpecies: ImageScaleView;
  private itemExpiration: LangBitmapFontTextView;
  private itemIcon: ImageScaleView;

  constructor(opts: { superview: View; close: (result?: boolean) => void }) {
    super({
      ...opts,
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 750,
      darkerBg: true,
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.REMINDER_CONFIRMATION_POPUP._,
      },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 26,
      y: 72,
      width: 496,
      height: 80,
    });

    new ImageView({
      ...style.dashedLine,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 160,
    });
    // Profile section.
    const profileBG = new ImageSliceView({
      ...style.profileBg,
      superview: this.box,
      x: this.box.style.width / 2 - style.profileBg.width / 2,
    });

    this.profilePicture = new ImageScaleView({
      ...style.profilePicture,
      superview: profileBG,
    });

    this.profileName = new LangBitmapFontTextView({
      ...style.profileName,
      superview: profileBG,
      width: profileBG.style.width - 268,
    });

    this.visits = new LangBitmapFontTextView({
      ...style.visitsLabel,
      superview: profileBG,
      width: profileBG.style.width - 268,
    });

    this.lastLogin = new LangBitmapFontTextView({
      ...style.lastLoginLabel,
      superview: profileBG,
    });

    // Pet section.
    const petBg = new ImageScaleView({
      ...style.pane,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 380,
      width: this.box.style.width - 16,
    });

    this.petName = new LangBitmapFontTextView({
      ...style.petName,
      superview: petBg,
      width: profileBG.style.width - 268,
    });

    this.petDetails = new LangBitmapFontTextView({
      ...style.petDetails,
      superview: petBg,
      width: profileBG.style.width - 268,
    });

    const backdrop = new ImageView({
      ...style.speciesIconBackdrop,
      superview: petBg,
    });

    this.petSpecies = new ImageScaleView({
      ...style.speciesIcon,
      superview: backdrop,
      image: `assets/species/giraffe.png`,
    });

    // Item section.
    const itemBG = new ImageScaleView({
      ...style.pane,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 500,
      width: this.box.style.width - 16,
    });

    this.itemExpiration = new LangBitmapFontTextView({
      ...style.itemName,
      superview: itemBG,
    });

    this.itemIcon = new ImageScaleView({
      ...style.itemIcon,
      superview: itemBG,
      image: uiConfig.buruburu.care.confirmation.careItemIcons['i_vowsCoffee'],
    });

    new ImageView({
      ...style.dashedLine,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 580,
    });

    new LangBitmapFontTextView({
      ...style.messageDisplayedLabel,
      superview: this.box,
      localeText: () => i18n('duo.reminderConfirmation.notice'),
    });

    // Buttons section.

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 - 255 / 2,
      y: this.box.style.height - 120 / 2,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.REMINDER_CONFIRMATION_POPUP.CLOSE,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 + 255 / 2,
      y: this.box.style.height - 120 / 2,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      localeText: () => i18n('duo.reminderConfirmation.send'),
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.REMINDER_CONFIRMATION_POPUP.CONFIRM,
      },
    });
  }

  public init(args: DuoFriend) {
    super.init({
      title: i18n('duo.reminderConfirmation.title'),
      message: i18n('duo.reminderConfirmation.message'),
    });

    this.profileName.text = args.profile.name;
    this.visits.text = i18n('duo.reminderConfirmation.visits', {
      visits: args.profile.visits,
      visited: args.profile.visited,
    });
    this.profilePicture.setImage(args.profile.photo);
    this.lastLogin.text = `${i18n(
      'duo.reminderConfirmation.lastLogin',
    )} ${getTimestampText(args.profile.lastLogin)}`;

    this.petSpecies.setImage(`assets/species/${args.pet.species}.png`);
    this.petName.text = args.pet.name;
    this.petDetails.text = i18n('duo.reminderConfirmation.petDetails', {
      species:
        Pet.ruleset.evolutions[args.pet.species]?.[
          `name_${getCreativeSuffix()}`
        ],
      age: args.pet.age,
    });

    const elapsedTime = StateObserver.now() - args.invitation.timestamp;
    const expiration = Math.max(0, Duo.ruleset.inviteExpiration - elapsedTime);
    const hh = Math.floor(expiration / duration({ hours: 1 }));
    const mm = Math.floor(
      (expiration % duration({ hours: 1 })) / duration({ minutes: 1 }),
    );

    this.itemIcon.setImage(
      uiConfig.buruburu.care.confirmation.careItemIcons[args.invitation.item],
    );
    this.itemExpiration.text = i18n('duo.reminderConfirmation.expiration', {
      hh,
      mm,
    });
  }
}
